import { createContext, useContext, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";
import axiosInstance from "../config/axiosInstance";

const SOCKET_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:3002"
    : "https://app.shopalli.com.br";

const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
  const { token } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const socketRef = useRef(null);
  const isChatOpenRef = useRef(false);

  useEffect(() => {
    if (!token) return;

    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ["websocket"],
    });

    // Auto-join all existing conversation rooms so new_message events arrive
    socket.on("connect", async () => {
      try {
        const res = await axiosInstance.get("/chat/conversations");
        const conversations = res.data.conversations || [];
        conversations.forEach((conv) => {
          socket.emit("join_conversation", { conversationId: conv.id });
        });
      } catch (err) {
        console.warn("[ChatContext] auto-join rooms failed:", err.message);
      }
    });

    socket.on("new_message", ({ message }) => {
      if (message.senderType === "CUSTOMER" && !isChatOpenRef.current) {
        setUnreadCount((n) => n + 1);
      }
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token]);

  const clearUnread = () => setUnreadCount(0);

  const setChatOpen = (open) => {
    isChatOpenRef.current = open;
    if (open) setUnreadCount(0);
  };

  return (
    <ChatContext.Provider value={{ socketRef, unreadCount, clearUnread, setChatOpen }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => useContext(ChatContext);
